/* eslint no-alert: 0 */
/* eslint no-console: 0 */

import Components from "../components";

// Parent class for select elements related to Trello
class TrelloSelect extends Components.Base {
  bind() {
    this.on("change", (e) => this.onSelectChanged(e));
    this.on($(document), "trello:authorized", (...args) =>
      this.onAuthorized(...args),
    );
    this.on($(document), "trello:deauthorized", () => this.onDeauthorized());
  }

  initialize() {
    // The select boxes have the "value" attribute set to their id,
    // even though there may not be an option at the moment with said id.
    // By copying the id into "trello-item-id" we ensure that the correct
    // option is selected when options are rendered.
    this.$el.data("trello-item-id", this.$el.attr("value"));
    this.$el.attr("disabled", true);
  }

  onAuthorized(e, args) {
    if (!this.trello) {
      this.trello = args.trello;
    }
    this.fetchOptions();
  }

  onDeauthorized() {
    this.$el.attr("disabled", true);
  }

  onSelectChanged(e) {
    const id = $(e.target).val();
    this.$el.data("trello-item-id", id);
    $(document).trigger(this.constructor.changeEventName, {
      id,
      name: $(e.target).find("option:selected").text(),
    });
  }

  fetchOptions() {
    throw Error.new("fetchOptions must be implemented by child class");
  }

  renderOptions(items) {
    if (!this.$el) {
      return;
    }
    this.$el.empty();
    items.forEach((item) => {
      this.$el.append(
        $("<option>", {
          value: item.id,
          text: item.name,
          selected: String(this.$el.data("trello-item-id")) === String(item.id),
        }),
      );
    });
    this.$el.removeAttr("disabled");
    this.$el.trigger("change");
  }

  renderError(response) {
    alert(
      `Error contacting the Trello API\n\n ${response.statusText}: ${
        response.responseText
      }\n\nPlease close the dialog and try again`,
    );
    this.trello.deauthorize(); // Force it, in case our cached authorization has been revoked
  }
}

// The select box holding the user's trello boards
class TrelloBoardSelect extends TrelloSelect {
  static initClass() {
    this.changeEventName = "trello:board:changed";
  }

  fetchOptions() {
    if (this.trello) {
      this.trello.members.get(
        "me/boards",
        (items) => this.renderOptions(items),
        (items) => this.renderError(items),
      );
    }
  }
}
TrelloBoardSelect.initClass();

// The select box holding the user's trello lists for the current board
class TrelloListSelect extends TrelloSelect {
  static initClass() {
    this.changeEventName = "trello:list:changed";
  }

  bind() {
    this.on($(document), TrelloBoardSelect.changeEventName, (e, args) =>
      this.fetchOptions(args.id),
    );
    super.bind();
  }

  fetchOptions(boardId) {
    if (boardId && this.trello) {
      this.trello.boards.get(
        `${boardId}/lists`,
        (items) => this.renderOptions(items),
        (items) => this.renderError(items),
      );
    }
  }
}
TrelloListSelect.initClass();

// Msc UI manipulation that happens during authorization
class TrelloForm extends Components.Base {
  bind() {
    this.on($(document), "trello:authorized", (...args) =>
      this.onAuthorized(...args),
    );
    this.on($(document), TrelloBoardSelect.changeEventName, (...args) =>
      this.onBoardChanged(...args),
    );
    this.on($(document), TrelloListSelect.changeEventName, (...args) =>
      this.onListChanged(...args),
    );
    this.on($(document), "trello:deauthorized", () => this.onDeauthorized());
  }

  onAuthorized(e, args) {
    this.$el.find("#channel_oauth_token").val(args.trello.token);
    this.$el.find("#connect-oauth-group").hide();
  }

  onDeauthorized() {
    this.$el.find("#channel_oauth_token").val("");
    this.$el.find("#connect-oauth-group").show();
  }

  onBoardChanged(e, args) {
    this.$el.find("#channel_board").val(args.name);
  }

  onListChanged(e, args) {
    this.$el.find("#channel_list").val(args.name);
  }
}

// The "authorize trello" button or link
class TrelloAuthorizer extends Components.Base {
  prepareDom() {
    this.trelloDependency = new Components.Dependency({
      klass: "Trello",
      url: `https://api.trello.com/1/client.js?key=${this.$el.data(
        "trello-key",
      )}`,
      onReady: () => this.onTrelloReady(),
    });
  }

  bind() {
    this.on("click.TrelloAuthorizer", (overrides) => this.authorize(overrides));
  }

  authorize(overrides) {
    if (window.trelloIsAuthorized) {
      $(document).trigger("trello:authorized", { trello: window.Trello });
    } else if (this.trelloDependency.isLoaded()) {
      window.Trello.authorize(this.optionsForAuthorize(overrides));
    }
  }

  optionsForAuthorize(overrides) {
    return $.extend(
      {
        type: "popup",
        name: "Honeybadger",
        expiration: "never",
        scope: {
          read: true,
          write: true,
          account: false,
        },
        success() {
          window.trelloIsAuthorized = true;
          $(document).trigger("trello:authorized", { trello: window.Trello });
        },
        error() {
          alert("Authorization failed.  Please try again.");
        },
      },
      overrides || {},
    );
  }

  onTrelloReady() {
    this.authorize({
      interactive: false,
      error() {
        if (console) {
          console.log("Trello not authorized on page load");
        }
      },
    });
  }
}

// The "disconnect oauth" button or link
class TrelloDeauthorizer extends Components.Base {
  initialize() {
    this.$el.hide();
  }

  bind() {
    this.on("click", () => this.deauthorize());
    this.on($(document), "trello:authorized", () => this.$el.show());
    this.on($(document), "trello:deauthorized", () => this.$el.hide());
  }

  deauthorize() {
    window.Trello.deauthorize();
    $(document).trigger("trello:deauthorized");
  }
}

Components.collection.register({
  selector: "form[data-kind=trello]",
  klass: TrelloForm,
});
Components.collection.register({
  selector: "form[data-kind=trello] #channel_board_id",
  klass: TrelloBoardSelect,
});
Components.collection.register({
  selector: "form[data-kind=trello] #channel_list_id",
  klass: TrelloListSelect,
});
Components.collection.register({
  selector: "form[data-kind=trello] #disconnect-oauth",
  klass: TrelloDeauthorizer,
});
Components.collection.register({
  selector: "[data-trello-key]",
  klass: TrelloAuthorizer,
});
